import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Check, Heart, MessageCircle, Send, Bookmark } from "lucide-react";
import { portfolio } from "../data/mock";

// Helper component for fast & progressive image loading with blurred backdrop option for social/branding items
const PortfolioImage = ({ src, alt, category }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isSocialOrBranding = category === "Social Media" || category === "Branding";

  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden bg-[#0e0d0b]">
      {/* Skeleton Shimmer */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-[#161513] via-[#22211f] to-[#161513] bg-[length:200%_100%] animate-shimmer flex items-center justify-center z-10">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--gold)]/20 border-t-[var(--gold)] animate-spin" />
        </div>
      )}

      {isSocialOrBranding ? (
        <div className="w-full h-full relative flex items-center justify-center p-2">
          {/* Blurred Backdrop */}
          <img
            src={src}
            alt=""
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-cover blur-2xl brightness-[0.35] transition-opacity duration-700 ${isLoaded ? "opacity-75" : "opacity-0"
              }`}
          />
          {/* Foreground Contain Image */}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            onLoad={() => setIsLoaded(true)}
            className={`relative z-10 max-w-full max-h-full object-contain transition-all duration-700 group-hover:scale-105 ${isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
              }`}
          />
        </div>
      ) : (
        /* Standard Cover Image for Websites, Films, etc. */
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0 ${isLoaded ? "opacity-60 group-hover:opacity-100" : "opacity-0 scale-105"
            }`}
        />
      )}
    </div>
  );
};

const responsiveWebsites = [
  {
    id: "resp-1",
    title: "Claura Couture",
    niche: "Luxury E-Commerce",
    image: "/works/websites/responsive/claura.png",
  },
  {
    id: "resp-2",
    title: "CroptFlyt",
    niche: "Agritech Platform",
    image: "/works/websites/responsive/croptflyt.png",
  },
  {
    id: "resp-3",
    title: "Gaagar Dairy & Sweet",
    niche: "Bespoke Curation",
    image: "/works/websites/responsive/gaagar.png",
  }
];

const Portfolio = () => {
  const [active, setActive] = useState(null);
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [hoveredSite, setHoveredSite] = useState(null);

  const handleCardClick = (p, e) => {
    e.preventDefault();
    if (p.category === "Websites" && p.url) {
      window.open(p.url, "_blank");
    } else if (p.youtubeEmbedUrl) {
      setLightboxVideo(p.youtubeEmbedUrl);
    } else {
      setLightboxImage(p.image);
    }
  };

  return (
    <section id="portfolio" className="section relative" style={{ background: "#0a0908" }}>
      <div className="container-xl">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="mt-5 font-display text-[clamp(40px,6vw,88px)] leading-[1.02] text-[var(--cream)] balance"
            >
              Recent <span className="italic gold-text">chapters</span><br />
              from the studio.
            </motion.h2>
          </div>
          <a href="/contact" className="btn-ghost" data-cursor="brief">
            Start a project
          </a>
        </div>

        {/* Grid masonry */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {portfolio
            .filter((p) => p.category === "Social Media")
            .map((p, i) => (
              <motion.div
                key={p.id}
                onClick={(e) => handleCardClick(p, e)}
                onMouseEnter={() => setActive(p.id)}
                onMouseLeave={() => setActive(null)}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="art-card group relative overflow-hidden rounded-2xl cursor-pointer border border-white/5 shadow-2xl"
                style={{ aspectRatio: "4 / 5" }}
                data-cursor="view"
              >
                <div className="w-full h-full flex flex-col justify-between bg-[#0e0d0b] border border-white/5 relative">
                  {/* 1. Header (Profile Mockup) */}
                  <div className="flex items-center justify-between p-3 border-b border-white/[0.05] bg-black/20 w-full z-20">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-impact bg-gradient-to-br from-[var(--gold)] to-[var(--copper)] text-black font-semibold">
                        {p.title.charAt(0)}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-display text-[11px] font-medium text-[var(--cream)] truncate max-w-[120px]">
                          {p.title.split(" ")[0]}
                        </span>
                        <span className="w-3 h-3 rounded-full bg-sky-500 flex items-center justify-center text-[6px] text-white">
                          <Check size={6} strokeWidth={4} />
                        </span>
                      </div>
                    </div>
                    <span className="font-impact text-[9px] tracking-wider text-[var(--gold)] uppercase bg-[var(--gold)]/10 px-2 py-0.5 rounded border border-[var(--gold)]/20">
                      POST
                    </span>
                  </div>

                  {/* 2. Visual Viewport (Center) */}
                  <div className="flex-1 w-full relative overflow-hidden bg-black/40">
                    <PortfolioImage src={p.image} alt={p.title} category={p.category} />
                  </div>

                  {/* 3. Instagram Interaction Bar */}
                  <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.02] bg-black/20 w-full z-20">
                    <div className="flex items-center gap-3 text-[var(--cream-muted)]">
                      <Heart size={14} className="hover:text-red-500 hover:fill-red-500 cursor-pointer transition-colors" />
                      <MessageCircle size={14} className="hover:text-[var(--gold)] cursor-pointer transition-colors" />
                      <Send size={14} className="hover:text-[var(--gold)] cursor-pointer transition-colors" />
                    </div>
                    <Bookmark size={14} className="text-[var(--cream-muted)] hover:text-[var(--gold)] cursor-pointer transition-colors" />
                  </div>

                  {/* 4. Footer Description */}
                  <div className="p-3 bg-black/40 border-t border-white/[0.03] z-20">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-[15px] font-semibold text-[var(--cream)] group-hover:text-[var(--gold)] transition-colors duration-300 truncate">
                          {p.title}
                        </h3>
                        <p className="text-[10px] text-[var(--cream-muted)] leading-relaxed font-body line-clamp-2 mt-0.5">
                          {p.desc}
                        </p>
                      </div>
                      {p.instagramUrl ? (
                        <a
                          href={p.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 hover:bg-[var(--gold)] hover:text-black flex items-center justify-center transition-all shrink-0 self-end"
                          onClick={(e) => e.stopPropagation()}
                          title="View on Instagram"
                        >
                          <ArrowUpRight size={14} />
                        </a>
                      ) : (
                        <span className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 self-end">
                          <ArrowUpRight size={14} className="text-[var(--cream-muted)]" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
        </div>

        {/* Mobile Responsive Showcase */}
        <div className="mt-36">
          <div className="flex items-center gap-4 mb-12">
            <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">02</span>
            <span className="w-16 h-px bg-[var(--rule)]" />
            <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">RESPONSIVE MOBILE ARCHITECTURE</span>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 max-w-5xl mx-auto justify-items-center">
            {responsiveWebsites.map((site) => (
              <div
                key={site.id}
                className="flex flex-col items-center group w-full"
                onMouseEnter={() => setHoveredSite(site.id)}
                onMouseLeave={() => setHoveredSite(null)}
              >
                {/* iPhone Device Mockup */}
                <div
                  className="relative w-[280px] h-[560px] rounded-[44px] bg-[#0c0a09] border-[10px] border-[#1d1d1f] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] overflow-hidden transition-transform duration-500 hover:scale-[1.02]"
                  style={{
                    outline: "1px solid rgba(255, 255, 255, 0.05)",
                  }}
                >
                  {/* Dynamic Island / Notch */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-4.5 bg-black rounded-full z-30 flex items-center justify-around px-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#111] border border-white/5" />
                    <span className="w-1 h-1 rounded-full bg-[#050505]" />
                  </div>

                  {/* Speaker Ear Piece Grill */}
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 w-12 h-1 bg-black rounded-full z-30" />

                  {/* Viewport Frame */}
                  <div className="w-full h-full relative overflow-hidden rounded-[34px] bg-[#090807]">
                    {/* Long Screenshot with hover scroll effect */}
                    <img
                      src={site.image}
                      alt={site.title}
                      className="w-full absolute left-0 top-0 transition-transform ease-in-out"
                      style={{
                        objectPosition: "top center",
                        transform: hoveredSite === site.id ? "translateY(calc(-100% + 540px))" : "translateY(0)",
                        transitionDuration: hoveredSite === site.id ? "12000ms" : "2500ms"
                      }}
                    />
                  </div>
                </div>

                {/* Device Title & Details */}
                <div className="mt-6 text-center">
                  <h4 className="font-display text-xl text-[var(--cream)] group-hover:text-[var(--gold)] transition-colors duration-300">
                    {site.title}
                  </h4>
                  <p className="text-[10px] font-impact tracking-wider text-[var(--cream-muted)] uppercase mt-1">
                    {site.niche}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {(lightboxImage || lightboxVideo) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setLightboxImage(null);
              setLightboxVideo(null);
            }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/90 backdrop-blur-xl"
          >
            <button
              onClick={() => {
                setLightboxImage(null);
                setLightboxVideo(null);
              }}
              className="absolute top-6 right-6 z-50 w-12 h-12 rounded-full bg-white/10 border border-white/20 text-white flex items-center justify-center hover:bg-[var(--gold)] hover:text-black transition-all"
            >
              <X size={20} />
            </button>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-4xl max-h-[88vh] rounded-2xl overflow-hidden border border-[var(--gold)]/30 shadow-[0_0_50px_rgba(201,169,97,0.15)] bg-[#0e0d0b]"
            >
              {lightboxVideo ? (
                <div className="aspect-video w-[85vw] max-w-4xl bg-black">
                  <iframe
                    src={lightboxVideo}
                    title="Featured Video"
                    className="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <img
                  src={lightboxImage}
                  alt="Portfolio Enlarge"
                  className="max-w-full max-h-[85vh] object-contain block mx-auto"
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)]">{label.toUpperCase()}</span>
  </div>
);

export default Portfolio;
